import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getCavityResistivity, getFrameResistivity } from "utils/enclosure/codeLayers";
import { getUdefFramingAdjustedEmbodiedCarbonDisplay } from "utils/enclosure/embc/udefStandardLayers";

import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Field from "components/Field";
import Accordion from "components/Accordion";
import AccordionActions from "../../AccordionActions";
import EmbodiedCarbonEditor from "components/EmbodiedCarbonGrade/UdefEmbodiedCarbonEditor";
import { GradeDisplay, PercentageDisplay } from "components/EmbodiedCarbonGrade";

import classes from "features/Model/Enclosure/UserDefinedCodes/style.module.scss";

const layerValidation = getValidation("udefStrappingLayer");

const strappingCategoryValidation = getValidation("udefStrappingMaterialCat");
const strappingTypeValidation = getValidation("udefStrappingMaterialType");
const strappingResistivityValidation = getValidation("udefStrappingResistivity");
const strappingThicknessValidation = getValidation("udefStrappingThickness");
const strappingRValueValidation = getValidation("udefStrappingRVal");
const strappingWidthValidation = getValidation("udefStrappingWidth");
const strappingSpacingValidation = getValidation("udefStrappingSpacing");

const cavityCategoryValidation = getValidation("udefStrappingCavityMaterialCat");
const cavityTypeValidation = getValidation("udefStrappingCavityMaterialType");
const cavityResistivityValidation = getValidation("udefStrappingCavityResistivity");
const cavityThicknessValidation = getValidation("udefStrappingCavityThickness");
const cavityRValueValidation = getValidation("udefStrappingCavityRVal");

const Strapping = React.memo(
    ({
        id,
        change,
        accessor,
        drawerAccessor,
        isDragging = false,
        dragHandleProps,
        dragActive = false,
        barMetric = "percentage",
    }) => {
        const dispatch = useDispatch();
        const framingLayer = useStateSelector(`${accessor}`);
        const { id: structureTypeId } = useStateSelector(`${accessor}.structureType`) || {};
        const { id: strappingMaterialCatId } = useStateSelector(`${accessor}.materialCategory`) || {};
        const { id: strappingMaterialTypeId } = useStateSelector(`${accessor}.materialType`) || {};
        const strappingResistivityValue = useStateSelector(`${accessor}.resistivity`);
        const strappingThicknessValue = useStateSelector(`${accessor}.thickness`);
        const strappingSpacingValue = useStateSelector(`${accessor}.spacing`);
        const strappingWidthValue = useStateSelector(`${accessor}.width`);
        const strappingLayers = useStateSelector(`${drawerAccessor}.uDefCode.layers.strapping`);

        const strappingRValue = parseFloat((strappingResistivityValue * strappingThicknessValue).toFixed(2));

        const { id: cavityMaterialCatId } = useStateSelector(`${accessor}.cavityInsulation.materialCategory`) || {};
        const { id: cavityMaterialTypeId } = useStateSelector(`${accessor}.cavityInsulation.materialType`) || {};
        const cavityResistivityValue = useStateSelector(`${accessor}.cavityInsulation.resistivity`);
        const cavityThicknessValue = useStateSelector(`${accessor}.cavityInsulation.thickness`);

        const cavityRValue = parseFloat((cavityResistivityValue * cavityThicknessValue).toFixed(2));

        const strappingResistivityUnits = useStateSelector(`${accessor}.resistivity_u`);
        const strappingThicknessUnits = useStateSelector(`${accessor}.thickness_u`);
        const strappingRValueUnits = useStateSelector(`${accessor}.rValue_u`);
        const strappingWidthUnits = useStateSelector(`${accessor}.width_u`);
        const strappingSpacingUnits = useStateSelector(`${accessor}.spacing_u`);

        const cavityResistivityUnits = useStateSelector(`${accessor}.cavityInsulation.resistivity_u`);
        const cavityThicknessUnits = useStateSelector(`${accessor}.cavityInsulation.thickness_u`);
        const cavityRValueUnits = useStateSelector(`${accessor}.cavityInsulation.rValue_u`);

        const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
        const layerIndex = useStateSelector(`${accessor}.layer`);

        const materialTypeOptions =
            getOptions({ fieldKey: "udefStrappingMaterialType", indKey: strappingMaterialCatId }) || [];

        const { label = "" } =
            materialTypeOptions.find(({ value: { id = 0 } = {} }) => id === strappingMaterialTypeId) || {};

        const deleteLayer = useCallback(() => {
            const { [id]: _, ...remainingLayers } = strappingLayers;
            change(`${drawerAccessor}.uDefCode.layers.strapping`, remainingLayers);
            dispatch(removeFormErrors(accessor));
        }, [id, strappingLayers, change, dispatch, drawerAccessor, accessor]);

        const { displayBase: baseRValUnits = "" } = getBaseUnits("udefStrappingRVal", modelUnits);
        const rValueUnits = strappingRValueUnits || baseRValUnits;
        const materialLabel = label || `User Defined: ${strappingRValue} ${rValueUnits}`;

        //ADD USER DEFINED DEPENDENCIES

        const display = useMemo(() => {
            return getUdefFramingAdjustedEmbodiedCarbonDisplay({ layerType: "strapping", layer: framingLayer });
        }, [framingLayer]);

        const showGrade = barMetric === "grade";

        const gradeInner = (
            <GradeDisplay
                emissions={display.emissions}
                storage={display.storage}
                emissionGrade={display.emissionGrade}
                storageGrade={display.storageGrade}
                withBorder={true}
                borderHeight={"100%"}
                borderColor={"#E1EAF0"}
                customGap={"0.5rem"}
                customPadding={"0 7px"}
                hasMismatch={display.hasMismatch}
            />
        );

        const percentageInner = (
            <PercentageDisplay
                emissions={display.emissions}
                storage={display.storage}
                withBorder={true}
                borderHeight={"100%"}
                borderColor={"#E1EAF0"}
                customGap={"0.5rem"}
                customPadding={"0 7px"}
                hasMismatch={display.hasMismatch}
            />
        );

        return (
            <div>
                <Accordion
                    className={classes.accordion}
                    heading={
                        <span className={classes.accordionHeader}>
                            <span className={classes.accordionPreTitle}>Strapping</span>
                            <span className={classes.accordionTitle}>{materialLabel}</span>
                        </span>
                    }
                    number={layerIndex}
                    isDragging={isDragging}
                    dragActive={dragActive}
                    hoverActions={
                        <AccordionActions
                            dragHandleProps={dragHandleProps}
                            accessor={accessor}
                            deleteLayer={deleteLayer}
                        />
                    }
                    bodyClassName={classes.accordionBodyContent}
                    carbonGrade={
                        <span className={classes.gradeContainer}>{showGrade ? gradeInner : percentageInner}</span>
                    }
                >
                    <div className={classes.inputFields}>
                        <h4>Strapping</h4>
                        <InputRow gridTemplate="1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.materialCategory`}
                                options={getOptions({ fieldKey: "udefStrappingMaterialCat" })}
                                label="Material Category"
                                placeholder="Choose Material Category"
                                validate={strappingCategoryValidation}
                                onChange={({ id }) => {
                                    if (id === 0) {
                                        change(`${accessor}.materialType`, { id: null });
                                    } else {
                                        change(`${accessor}.materialType`, { id: 0 });
                                    }
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.materialType`}
                                options={getOptions({
                                    fieldKey: "udefStrappingMaterialType",
                                    indKey: strappingMaterialCatId,
                                })}
                                disabled={strappingMaterialCatId === 0}
                                label="Material Type"
                                placeholder="Choose Material Type"
                                validate={strappingTypeValidation}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.resistivity`}
                                label="Resistivity"
                                placeholder={0.0}
                                disabled={strappingMaterialCatId !== 0}
                                validate={strappingResistivityValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingResistivity")}
                                change={change}
                                setValue={
                                    strappingMaterialTypeId == null
                                        ? strappingResistivityValue
                                        : getFrameResistivity(strappingMaterialTypeId)
                                }
                                units={{
                                    base: getBaseUnits("udefStrappingResistivity", modelUnits),
                                    options: getUnitOptions("thermalResistivity"),
                                    selected: strappingResistivityUnits,
                                    unitType: "thermalResistivity",
                                    accessor: `${accessor}.resistivity_u`,
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.thickness`}
                                label="Thickness"
                                placeholder={0.0}
                                validate={strappingThicknessValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingThickness")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingThickness", modelUnits),
                                    options: getUnitOptions("length"),
                                    selected: strappingThicknessUnits,
                                    unitType: "length",
                                    accessor: `${accessor}.thickness_u`,
                                }}
                                warning={strappingThicknessValue === 0 ? "strappingZero" : ""}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.rValue`}
                                label="R-Value"
                                placeholder={0.0}
                                disabled={true}
                                setValue={strappingRValue}
                                validate={strappingRValueValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingRVal")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingRVal", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: strappingRValueUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.rValue_u`,
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.width`}
                                label="Width"
                                placeholder={0.0}
                                validate={strappingWidthValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingWidth")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingWidth", modelUnits),
                                    options: getUnitOptions("length"),
                                    selected: strappingWidthUnits,
                                    unitType: "length",
                                    accessor: `${accessor}.width_u`,
                                }}
                                warning={strappingWidthValue === 0 ? "strappingZero" : ""}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.spacing`}
                                label="Spacing"
                                placeholder={0.0}
                                validate={strappingSpacingValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingSpacing")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingSpacing", modelUnits),
                                    options: getUnitOptions("length"),
                                    selected: strappingSpacingUnits,
                                    unitType: "length",
                                    accessor: `${accessor}.spacing_u`,
                                }}
                                warning={strappingSpacingValue === 0 ? "strappingZero" : ""}
                            />
                        </InputRow>
                        <h4>Cavity Insulation</h4>
                        <InputRow gridTemplate="1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.cavityInsulation.materialCategory`}
                                options={getOptions({ fieldKey: "udefStrappingCavityMaterialCat" })}
                                label="Material Category"
                                placeholder="Choose Material Category"
                                validate={cavityCategoryValidation}
                                onChange={({ id }) => {
                                    if (id === 0) {
                                        change(`${accessor}.cavityInsulation.materialType`, { id: null });
                                    } else {
                                        change(
                                            `${accessor}.cavityInsulation.materialType`,
                                            getFirstOption({ indKey: id, depKey: "udefStrappingCavityMaterialType" })
                                        );
                                    }
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.cavityInsulation.materialType`}
                                options={getOptions({
                                    fieldKey: "udefStrappingCavityMaterialType",
                                    indKey: cavityMaterialCatId,
                                })}
                                disabled={cavityMaterialCatId === 0}
                                label="Material Type"
                                placeholder="Choose Material Type"
                                validate={cavityTypeValidation}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.cavityInsulation.resistivity`}
                                label="Resistivity"
                                placeholder={0.0}
                                disabled={cavityMaterialCatId !== 0}
                                validate={cavityResistivityValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingCavityResistivity")}
                                change={change}
                                setValue={
                                    cavityMaterialTypeId == null
                                        ? cavityResistivityValue
                                        : getCavityResistivity(cavityMaterialTypeId)
                                }
                                units={{
                                    base: getBaseUnits("udefStrappingCavityResistivity", modelUnits),
                                    options: getUnitOptions("thermalResistivity"),
                                    selected: cavityResistivityUnits,
                                    unitType: "thermalResistivity",
                                    accessor: `${accessor}.cavityInsulation.resistivity_u`,
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.cavityInsulation.thickness`}
                                label="Thickness"
                                placeholder={0.0}
                                validate={cavityThicknessValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingCavityThickness")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingCavityThickness", modelUnits),
                                    options: getUnitOptions("length"),
                                    selected: cavityThicknessUnits,
                                    unitType: "length",
                                    accessor: `${accessor}.cavityInsulation.thickness_u`,
                                }}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.cavityInsulation.rValue`}
                                label="R-Value"
                                placeholder={0.0}
                                disabled={true}
                                setValue={cavityRValue}
                                validate={cavityRValueValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefStrappingCavityRVal")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefStrappingCavityRVal", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: cavityRValueUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.cavityInsulation.rValue_u`,
                                }}
                            />
                        </InputRow>
                    </div>
                    <div className={classes.embodiedCarbonEditor}>
                        <EmbodiedCarbonEditor
                            change={change}
                            display={display}
                            layersFieldPrefix={accessor}
                            layerType={"strapping"}
                            layer={framingLayer}
                        />
                    </div>
                </Accordion>
            </div>
        );
    }
);

export default Strapping;
